/**
 * Class Service
 * 백엔드 API 변경: Course → Class (2026-02-02)
 * Swagger: /class/* (기존 /course/*)
 * @see my-app/docs/api/API.md
 */

import { apiClient, ApiError, logErrorResponseData, logResponseData } from './apiClient';
import { isPublicShoppingBrowsePath } from '@/lib/publicShoppingPaths';
import {
	normalizeGetClassDetailPayload,
	normalizeGetEnrolledClassListPayload,
	normalizeGetOpenClassListPayload,
} from '../utils/classApiNormalize';
import type {
	GetClassListByOrganizationResponseDto,
	GetOpenClassListResponseDto,
	GetEnrolledClassListResponseDto,
	CreateClassRequestDto,
	CreateClassResponseDto,
	GetClassDetailResponseDto,
	GetOrderUserCountResponseDto,
	GetClassUserListResponseDto,
	UpdateClassRequestDto,
	UpdateClassResponseDto,
	DeleteClassResponseDto,
	RemoveClassParticipantResponseDto,
	EnrollClassResponseDto,
	UpdateClassPasswordRequestDto,
	UpdateClassPasswordResponseDto,
	ConfirmJoinClassResponseDto,
	CreateClassInviteRequestDto,
	CreateClassInviteResponseDto,
	VerifyClassInviteResponseDto,
	AcceptClassInviteResponseDto,
	VerifyAccessCodeRequestDto,
	VerifyAccessCodeResponseDto,
	EnrolledClassStatus,
	EnrolledClassListItemDto,
	OpenClassListItemDto,
} from '../types/api/class';

export interface GetClassListByOrganizationParams {
	organizationId?: number;
	page: number;        // 필수 (API 문서)
	pageSize: number;    // 필수 (API 문서)
	startDate?: string;
	endDate?: string;
	isActive?: boolean;
	isOpenClass?: boolean;
	title?: string;
	language?: string;
}

/**
 * Swagger: GET /class/open/list 오픈클래스 리스트 조회 API
 * — 쿼리는 page·pageSize 필수, language·searchText·scenarioDeviceTypeId 선택.
 * (과거 deviceType 문자열 파라미터는 스펙에 없어 제거함)
 */
export interface GetOpenClassListParams {
	page: number;
	pageSize: number;
	searchText?: string;
	scenarioDeviceTypeId?: number;
	language?: string;
}

/** Swagger: GET /class/enrolled/list — 본인 수강 중인 클래스 (role별 스코프는 백엔드 정책) */
export interface GetEnrolledClassListParams {
	page: number;
	pageSize: number;
	status?: EnrolledClassStatus;
	language?: string;
}

/**
 * 기관별 클래스 리스트 조회
 * Swagger: GET /class/organization/list
 * - role 2 (master): 본인 기관만 조회
 * - role 5 (admin): organizationId 있으면 해당 기관, 없으면 전체 조회
 *
 * 쿼리 불리언(isOpenClass, isActive): GET 쿼리에는 "true"/"false" 문자열로 전달됩니다.
 * 클래스 관리 화면: isOpenClass=true(오픈만)·false(기관만)·미전달(전체)로 서버 필터링합니다.
 * Nest 등에서 거부 시 @Transform / ParseBoolPipe 등으로 문자열을 boolean으로 변환해야 합니다.
 */
export const getClassListByOrganization = async (
	params: GetClassListByOrganizationParams
): Promise<GetClassListByOrganizationResponseDto> => {
	const { page = 1, pageSize = 20, isOpenClass, language, ...other } = params;
	const requestParams: Record<string, string | number | boolean | undefined> = {
		page,
		pageSize,
		...other,
	};
	/** `isOpenClass` 는 rest boolean 그대로보다 "true"/"false" 명시(일부 백엔드/게이트웨이에서 false 생략 시 ‘전체’·오픈 쪽으로 떨어짐) */
	if (isOpenClass === true) {
		requestParams.isOpenClass = 'true';
	} else if (isOpenClass === false) {
		requestParams.isOpenClass = 'false';
	}
	if (language) {
		requestParams.language = language;
	}
	const response = await apiClient.get<GetClassListByOrganizationResponseDto>(`/class/organization/list`, {
		params: requestParams,
	});
	return response;
};

/**
 * 오픈클래스 목록 (공개) — GET /class/open/list 만 사용
 */
export const getOpenClassList = async (
	params: GetOpenClassListParams
): Promise<GetOpenClassListResponseDto> => {
	const { page, pageSize, searchText, scenarioDeviceTypeId, language } = params;
	const raw = await apiClient.get<unknown>(`/class/open/list`, {
		/** 공개 오픈클래스 목록은 로그인 여부와 무관하게 접근 */
		skipAuth: true,
		params: {
			page,
			pageSize,
			...(searchText?.trim() ? { searchText: searchText.trim() } : {}),
			...(scenarioDeviceTypeId != null ? { scenarioDeviceTypeId } : {}),
			...(language ? { language } : {}),
		},
	});
	return normalizeGetOpenClassListPayload(raw);
};

/**
 * 로그인 사용자 기준 오픈클래스 목록 — 동일하게 GET /class/open/list (Bearer 시 수강 여부 등)
 */
export const getOpenClassListAuthenticated = async (
	params: GetOpenClassListParams
): Promise<GetOpenClassListResponseDto> => {
	const { page, pageSize, searchText, scenarioDeviceTypeId, language } = params;
	const raw = await apiClient.get<unknown>(`/class/open/list`, {
		skipAuth: false,
		params: {
			page,
			pageSize,
			...(searchText?.trim() ? { searchText: searchText.trim() } : {}),
			...(scenarioDeviceTypeId != null ? { scenarioDeviceTypeId } : {}),
			...(language ? { language } : {}),
		},
	});
	return normalizeGetOpenClassListPayload(raw);
};

const ALL_ENROLLED_STATUSES: EnrolledClassStatus[] = ['AVAILABLE', 'IN_PROGRESS', 'COMPLETED', 'EXPIRED'];

function mergeEnrolledRows(byId: Map<number, EnrolledClassListItemDto>, rows: EnrolledClassListItemDto[] | undefined) {
	for (const row of rows ?? []) {
		if (!byId.has(row.classId)) byId.set(row.classId, row);
	}
}

/** 인증 오픈 목록 전체 페이지 순회 — 수강 표시가 뒤 페이지에만 있을 수 있음 */
async function fetchOpenEnrolledAcrossPages(
	pageSize: number,
	maxPages: number,
	language?: string
): Promise<Map<number, OpenClassListItemDto>> {
	const enrolledOpen = new Map<number, OpenClassListItemDto>();
	let page = 1;
	while (page <= maxPages) {
		try {
			const res = await getOpenClassListAuthenticated({ page, pageSize, ...(language ? { language } : {}) });
			const chunk = res.classList ?? [];
			for (const o of chunk) {
				if (o.isEnrolled) enrolledOpen.set(o.classId, o);
			}
			if (chunk.length < pageSize) break;
			page++;
		} catch {
			break;
		}
	}
	return enrolledOpen;
}

/**
 * 수강생 마이클래스용:
 * 1) status 없이 enrolled/list (서버가 전체를 주는 경우)
 * 2) 상태별 enrolled/list 합집합 (일부만 내려주는 서버 보강)
 * 3) 인증 오픈 목록 전 페이지에서 isEnrolled 보강
 */
export async function fetchStudentMyClassesMerged(params: {
	page: number;
	pageSize: number;
	language?: string;
}): Promise<GetEnrolledClassListResponseDto> {
	const { language, ...pageParams } = params;
	const byId = new Map<number, EnrolledClassListItemDto>();
	let hadAnyEnrolledHttpOk = false;

	try {
		const unfiltered = await getEnrolledClassList({ ...pageParams, ...(language ? { language } : {}) });
		mergeEnrolledRows(byId, unfiltered.classList);
		hadAnyEnrolledHttpOk = true;
	} catch {
		/* status 없는 단일 조회 미지원·오류 시 상태별로만 시도 */
	}

	const settled = await Promise.allSettled(
		ALL_ENROLLED_STATUSES.map((status) =>
			getEnrolledClassList({ ...pageParams, status, ...(language ? { language } : {}) })
		)
	);
	for (const r of settled) {
		if (r.status !== 'fulfilled') continue;
		hadAnyEnrolledHttpOk = true;
		mergeEnrolledRows(byId, r.value.classList);
	}
	/** 수강 0건이 정상일 수 있음. enrolled API가 전부 실패했을 때만 오류 전달 */
	if (byId.size === 0 && !hadAnyEnrolledHttpOk) {
		const rej = settled.find((s) => s.status === 'rejected') as PromiseRejectedResult | undefined;
		if (rej?.reason instanceof ApiError) throw rej.reason;
		if (rej?.reason instanceof Error) throw rej.reason;
	}

	try {
		const openEnrolled = await fetchOpenEnrolledAcrossPages(params.pageSize, 30, language);
		for (const o of openEnrolled.values()) {
			if (byId.has(o.classId)) continue;
			const syn: EnrolledClassListItemDto = {
				classId: o.classId,
				title: o.title ?? '',
				thumbnailImageUrl: o.thumbnailImageUrl ?? null,
				scenarioCount: 0,
				enrolledAt: null,
				expiresAt: null,
				classStartDate: null,
				classEndDate: null,
				isCompleted: false,
				isActive: true,
				isOpenClass: true,
				status: 'IN_PROGRESS',
			};
			byId.set(o.classId, syn);
		}
	} catch {
		/* 오픈 목록 보강 실패 시 enrolled만 사용 */
	}

	const classList = Array.from(byId.values());
	return { classList, totalCount: classList.length };
}

/**
 * 수강 중인 클래스 목록
 * Swagger: GET /class/enrolled/list
 */
export const getEnrolledClassList = async (
	params: GetEnrolledClassListParams
): Promise<GetEnrolledClassListResponseDto> => {
	const { page, pageSize, status, language } = params;
	const raw = await apiClient.get<unknown>(`/class/enrolled/list`, {
		params: {
			page,
			pageSize,
			...(status ? { status } : {}),
			...(language ? { language } : {}),
		},
	});
	return normalizeGetEnrolledClassListPayload(raw);
};

/**
 * 클래스 상세 조회
 * Swagger: GET /class/{classId}/detail
 */
export const getClassDetail = async (
	classId: number,
	opts?: {
		signal?: AbortSignal;
		language?: string;
	}
): Promise<GetClassDetailResponseDto> => {
	const fetchDetail = (skipAuth: boolean) =>
		apiClient.get<unknown>(`/class/${classId}/detail`, {
			skipAuth,
			signal: opts?.signal,
			params: opts?.language ? { language: opts.language } : {},
		});

	const finalize = async (raw: unknown) => {
		const normalized = normalizeGetClassDetailPayload(raw, classId);
		logResponseData({
			'GET /class/detail': `/class/${classId}/detail`,
			response: raw,
			normalized,
		});
		return normalized;
	};

	/** 로그인 시 토큰 전달 — 백엔드가 상세를 인증 필수로 두는 경우 필요 */
	if (apiClient.hasTokens()) {
		try {
			const raw = await fetchDetail(false);
			return await finalize(raw);
		} catch (e) {
			const is401 = e instanceof ApiError && e.status === 401;
			const is403 = e instanceof ApiError && e.status === 403;
			const onPublic =
				typeof window !== 'undefined' && isPublicShoppingBrowsePath(window.location.pathname);
			/**
			 * 공개 쇼핑 경로: 인증 요청이 401(만료)·403(역할 정책)이면 비인증 재시도.
			 * 마스터 등 일부 역할은 토큰 부착 시 GET /class/{id}/detail 만 거절되는 환경이 있음.
			 */
			if ((is401 || is403) && onPublic) {
				const raw = await fetchDetail(true);
				return await finalize(raw);
			}
			throw e;
		}
	}

	const raw = await fetchDetail(true);
	return await finalize(raw);
};

/**
 * 클래스 참여자 목록 조회
 * Swagger: GET /class/{classId}/user-list
 */
export const getClassUserList = async (
	classId: number,
	params: { page?: number; pageSize?: number } = {}
): Promise<GetClassUserListResponseDto> => {
	const { page = 1, pageSize = 100 } = params;
	return await apiClient.get<GetClassUserListResponseDto>(
		`/class/${classId}/user-list`,
		{ params: { page, pageSize } }
	);
};

/**
 * 클래스에서 참가자 제외(수강 매핑 비활성 등 — 백엔드 스펙)
 * Swagger: PATCH /class/{classId}/user/{userId}/remove (ClassController_removeUserFromClass)
 */
export const removeClassParticipant = async (
	classId: number,
	userId: number
): Promise<RemoveClassParticipantResponseDto> => {
	return await apiClient.patch<RemoveClassParticipantResponseDto>(
		`/class/${classId}/user/${userId}/remove`,
		{}
	);
};

/**
 * 주문별 클래스 등록 가능 인원 조회
 * Swagger: GET /class/order/{orderId}/product-price/{productPriceId}/user-count
 */
export const getOrderUserCount = async (
	orderId: number,
	productPriceId: number
): Promise<GetOrderUserCountResponseDto> => {
	const path = `/class/order/${orderId}/product-price/${productPriceId}/user-count`;
	if (process.env.NODE_ENV === 'development') {
		console.log('[GET order user-count request]', { orderId, productPriceId, path });
	}
	try {
		const response = await apiClient.get<GetOrderUserCountResponseDto>(path);
		if (process.env.NODE_ENV === 'development') {
			console.log(response);
		}
		return response;
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			if (e instanceof ApiError) console.log(e.data);
			else console.log(e);
		}
		throw e;
	}
};

/**
 * 클래스 생성
 * Swagger: POST /class
 */
export const createClass = async (
	data: CreateClassRequestDto
): Promise<CreateClassResponseDto> => {
	try {
		if (process.env.NODE_ENV === 'development') {
			console.log('[POST /class request]', data);
		}
		const response = await apiClient.post<CreateClassResponseDto>(`/class`, data);
		if (process.env.NODE_ENV === 'development') {
			console.log(response);
		}
		return response;
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			if (e instanceof ApiError) console.log(e.data);
			else console.log(e);
		} else {
			logErrorResponseData(e);
		}
		throw e;
	}
};

/**
 * 클래스 수정
 * Swagger: PUT /class/{classId}
 */
export const updateClass = async (
	classId: number,
	data: UpdateClassRequestDto
): Promise<UpdateClassResponseDto> => {
	const response = await apiClient.put<UpdateClassResponseDto>(
		`/class/${classId}`,
		data
	);
	return response;
};

/**
 * 클래스 삭제
 * Swagger: DELETE /class/{classId} (Request Body: DeleteClassRequestDto)
 */
export const deleteClass = async (
	classId: number
): Promise<DeleteClassResponseDto> => {
	const response = await apiClient.delete<DeleteClassResponseDto>(
		`/class/${classId}`
	);
	return response;
};

/**
 * 클래스 수강신청
 * Swagger: POST /class/{classId}/enroll
 */
export const enrollClass = async (
	classId: number
): Promise<EnrollClassResponseDto> => {
	try {
		const response = await apiClient.post<EnrollClassResponseDto>(
			`/class/${classId}/enroll`
		);
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			if (error.status === 409) throw new Error('이미 수강신청한 클래스입니다.');
			if (error.status === 403) throw new Error('수강신청 권한이 없습니다.');
			if (error.status === 404) throw new Error('존재하지 않는 클래스입니다.');
			if (error.status === 400) throw new Error('라이센스 이용자 수가 초과되었습니다.');
		}
		throw error;
	}
};

/**
 * 클래스에 기관 기기 여러 대 할당
 * POST /class/{classId}/devices — 본문: `{ deviceIds: number[] }`
 */
export const assignClassDevices = async (
	classId: number,
	organizationDeviceIds: number[]
): Promise<{ message: string }> => {
	if (organizationDeviceIds.length === 0) {
		return { message: 'ok' };
	}
	const path = `/class/${classId}/devices`;
	const body = { deviceIds: organizationDeviceIds };
	try {
		const res = await apiClient.post<{ message: string }>(path, body);
		return res;
	} catch (e) {
		logErrorResponseData(e);
		throw e;
	}
};

/**
 * 클래스에 할당된 기관 기기 해제
 * DELETE /class/{classId}/devices — 본문: `{ deviceIds: number[] }`
 */
export const unassignClassDevices = async (
	classId: number,
	organizationDeviceIds: number[]
): Promise<{ message: string }> => {
	if (organizationDeviceIds.length === 0) {
		return { message: 'ok' };
	}
	const path = `/class/${classId}/devices`;
	const body = { deviceIds: organizationDeviceIds };
	try {
		const res = await apiClient.delete<{ message: string }>(path, body);
		return res;
	} catch (e) {
		logErrorResponseData(e);
		throw e;
	}
};

/** @deprecated `assignClassDevices(classId, [id])` 사용 권장 */
export const registerClassDevice = async (classId: number, organizationDeviceId: number): Promise<{ message: string }> => {
	return assignClassDevices(classId, [organizationDeviceId]);
};

/**
 * 클래스 비밀번호 변경
 * Swagger: PUT /class/{classId}/password
 */
export const updateClassPassword = async (
	classId: number,
	pw: string
): Promise<UpdateClassPasswordResponseDto> => {
	const body: UpdateClassPasswordRequestDto = { pw };
	return await apiClient.put<UpdateClassPasswordResponseDto>(
		`/class/${classId}/password`,
		body
	);
};

/** Swagger: POST /class/{classId}/confirm-join */
export const confirmJoinClass = async (
	classId: number
): Promise<ConfirmJoinClassResponseDto> => {
	return await apiClient.post<ConfirmJoinClassResponseDto>(
		`/class/${classId}/confirm-join`
	);
};

/** Swagger: POST /class/{classId}/invite */
export const createClassInvite = async (
	classId: number,
	body?: CreateClassInviteRequestDto
): Promise<CreateClassInviteResponseDto> => {
	return await apiClient.post<CreateClassInviteResponseDto>(
		`/class/${classId}/invite`,
		body ?? {}
	);
};

/** Swagger: GET /class/{classId}/invite/active */
export const getActiveClassInvite = async (
	classId: number
): Promise<CreateClassInviteResponseDto> => {
	return await apiClient.get<CreateClassInviteResponseDto>(
		`/class/${classId}/invite/active`
	);
};

/** Swagger: GET /class/invite/{token}/verify (비로그인 시 Authorization 미포함) */
export const verifyClassInvite = async (
	token: string,
	opts?: { signal?: AbortSignal; language?: string }
): Promise<VerifyClassInviteResponseDto> => {
	const enc = encodeURIComponent(token);
	return await apiClient.get<VerifyClassInviteResponseDto>(
		`/class/invite/${enc}/verify`,
		{
			signal: opts?.signal,
			params: opts?.language ? { language: opts.language } : {},
		}
	);
};

/** Swagger: POST /class/verify-access-code */
export const verifyAccessCode = async (
	body: VerifyAccessCodeRequestDto
): Promise<VerifyAccessCodeResponseDto> => {
	return await apiClient.post<VerifyAccessCodeResponseDto>(
		`/class/verify-access-code`,
		body
	);
};

/** Swagger: POST /class/invite/{token}/accept */
export const acceptClassInvite = async (
	token: string
): Promise<AcceptClassInviteResponseDto> => {
	const enc = encodeURIComponent(token);
	return await apiClient.post<AcceptClassInviteResponseDto>(
		`/class/invite/${enc}/accept`
	);
};

/**
 * 클래스에 유저 일괄 할당
 * Swagger: POST /class/{classId}/users/bulk-assign
 */
export interface BulkAssignUsersRequestDto {
	loginIds: string[];
}

export interface BulkAssignUsersResponseDto {
	assignedCount: number;
	skippedCount: number;
	notFoundLoginIds: string[];
	organizationMismatchLoginIds: string[];
}

export const bulkAssignUsersToClass = async (
	classId: number,
	loginIds: string[]
): Promise<BulkAssignUsersResponseDto> => {
	try {
		if (process.env.NODE_ENV === 'development') {
			console.log('[POST /class/users/bulk-assign request]', { classId, loginIds });
		}
		const response = await apiClient.post<BulkAssignUsersResponseDto>(
			`/class/${classId}/users/bulk-assign`,
			{ loginIds }
		);
		if (process.env.NODE_ENV === 'development') {
			console.log(response);
		}
		return response;
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			if (e instanceof ApiError) console.log(e.data);
			else console.log(e);
		} else {
			logErrorResponseData(e);
		}
		throw e;
	}
};

/**
 * GET /class/lab/info/{classId}
 * 기존 플랫폼과의 호환을 위해 MongoDB ObjectId를 포함한 클래스 Lab 정보 조회.
 * content._id를 통해 플랫폼 API 호출 시 사용할 시나리오 식별자를 얻습니다.
 * 
 * @returns platformGet이 data 필드를 자동으로 언래핑하므로 ClassLabInfoDto를 반환
 */
export const getClassLabInfo = async (
	classId: number | string,
	opts?: { signal?: AbortSignal; accessToken?: string }
): Promise<import('../types/api/class').ClassLabInfoDto> => {
	const { getStudyResultPlatformBaseUrl } = await import('../config/studyResultPlatformBaseUrl');
	
	const base = getStudyResultPlatformBaseUrl();
	const url = `${base.replace(/\/+$/, '')}/class/lab/info/${classId}`;
	
	const headers: Record<string, string> = {
		Accept: 'application/json',
		'x-access-from-medicrew-web': 'true',
		'x-medicrew-type': 'MEDICREW',
		'x-client-version': process.env.REACT_APP_MEDICREW_CLIENT_VERSION?.trim() || '2.4.0',
		'x-class-id': String(classId),
	};
	
	// Authorization: 암호화된 토큰을 그대로 전송 (Bearer 접두사 없이, URL 인코딩된 상태 그대로)
	let tokenToUse: string | undefined;
	if (opts?.accessToken !== undefined && opts.accessToken.trim() !== '') {
		tokenToUse = opts.accessToken.trim();
	} else {
		const token = apiClient.getAccessToken();
		if (token) {
			tokenToUse = token;
		}
	}
	
	if (process.env.NODE_ENV === 'development') {
		console.log('[getClassLabInfo] Authorization 토큰:', tokenToUse ? `존재 (길이: ${tokenToUse.length})` : '없음');
		if (tokenToUse) {
			console.log('[getClassLabInfo] 토큰 시작:', tokenToUse.substring(0, 50));
		}
	}
	
	if (tokenToUse) {
		// 토큰이 이미 URL 인코딩되어 있는지 확인 (%가 있으면 이미 인코딩됨)
		// 없으면 URL 인코딩 필요
		const needsEncoding = !tokenToUse.includes('%');
		if (needsEncoding) {
			if (process.env.NODE_ENV === 'development') {
				console.log('[getClassLabInfo] 토큰 URL 인코딩 중...');
			}
			headers.Authorization = encodeURIComponent(tokenToUse);
		} else {
			headers.Authorization = tokenToUse;
		}
	}
	
	const timeoutMs = 45_000;
	const controller = new AbortController();
	const tid = setTimeout(() => controller.abort(), timeoutMs);
	
	try {
		const res = await fetch(url, {
			method: 'GET',
			headers,
			signal: opts?.signal || controller.signal,
		});
		
		if (process.env.NODE_ENV === 'development') {
			console.log('[getClassLabInfo] HTTP Status:', res.status, res.statusText);
		}
		
		const text = await res.text();
		
		if (process.env.NODE_ENV === 'development') {
			console.log('[getClassLabInfo] Response text:', text.substring(0, 200));
		}
		
		let parsed: unknown;
		try {
			parsed = text ? JSON.parse(text) : null;
		} catch {
			console.error('[getClassLabInfo] JSON 파싱 실패:', text);
			throw new Error('Platform API 응답이 JSON이 아닙니다.');
		}
		
		if (!res.ok) {
			console.error('[getClassLabInfo] HTTP 에러:', res.status, parsed);
			throw new Error(`Platform API ${res.status}: ${res.statusText}`);
		}
		
		// data 필드 언래핑
		const unwrapped = typeof parsed === 'object' && parsed != null && 'data' in parsed
			? (parsed as { data: unknown }).data
			: parsed;
		
		logResponseData({
			'GET /class/lab/info': `class/lab/info/${classId}`,
			response: unwrapped,
		});
		
		return unwrapped as import('../types/api/class').ClassLabInfoDto;
	} catch (e) {
		if (e instanceof Error && e.name === 'AbortError') {
			throw new Error('요청 시간이 초과되었습니다.');
		}
		throw e;
	} finally {
		clearTimeout(tid);
	}
};
